/**
 * user-directory — pure filter / sort / count helpers for the Users page (USR-02, USR-05 … USR-07).
 *
 * Kept out of the page component so the count arithmetic and the tab semantics are unit-testable
 * without rendering the table.
 *
 * TAB SEMANTICS (USR-02 — verified, not assumed): the "active" tab calls
 * `GET /api/users/manage?enabled=true`, which the backend translates to `WHERE is_active = true`
 * (packages/users/handlers/list-users.ts → `listUsers`). An invited user has `is_active = true` and
 * a Cognito status of FORCE_CHANGE_PASSWORD, which `deriveStatus` maps to `'invited'`
 * (packages/users/service.ts). So the tab means "not deactivated" — active AND invited — by design.
 * An invited row appearing there is NOT a filter leak, so the fix is naming + counting, not query
 * logic.
 */
import type { DeliverProUser, DeliverProUserRole } from '@/types';
import { sortTextOptions } from '@/lib/filter-options';

export type UserTab = 'active' | 'deactivated';

/** Columns the directory tables can be sorted by. */
export type UserSortKey = 'full_name' | 'email' | 'role' | 'status';
export type UserSortDir = 'asc' | 'desc';

/** Roles a user can hold. Declared in source order; ordered for display by ROLE_FILTER_OPTIONS. */
export const USER_ROLES: readonly DeliverProUserRole[] = ['PM', 'SA', 'Leadership'];

/**
 * Role filter values in display order — A–Z, with the "All roles" sentinel injected first by the
 * call site (same convention as the Projects/Intakes toolbars). USR-07.
 */
export const ROLE_FILTER_OPTIONS: DeliverProUserRole[] = sortTextOptions(
  USER_ROLES,
) as DeliverProUserRole[];

/**
 * What each tab actually contains, spelled out for the UI so the label cannot drift from the query.
 * See the TAB SEMANTICS note above.
 */
export const TAB_NOUN: Record<UserTab, string> = {
  active: 'active & invited',
  deactivated: 'deactivated',
};

/** Free-text (name/email) + role multi-select (empty set = all roles). */
export function filterUsers(
  users: readonly DeliverProUser[],
  search: string,
  roleFilter: ReadonlySet<DeliverProUserRole>,
): DeliverProUser[] {
  const q = search.trim().toLowerCase();
  return users.filter((u) => {
    const matchesSearch =
      !q || u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    const matchesRole = roleFilter.size === 0 || roleFilter.has(u.role);
    return matchesSearch && matchesRole;
  });
}

/**
 * Stable sort by one column. Same comparator shape as the Projects table: nullish values sort last
 * regardless of direction, everything else compares case-insensitively.
 */
export function sortUsers(
  users: readonly DeliverProUser[],
  sortKey: UserSortKey,
  sortDir: UserSortDir,
): DeliverProUser[] {
  const dir = sortDir === 'asc' ? 1 : -1;
  return [...users].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
  });
}

/**
 * Sentence for the header count, so the badge can never be read as "some other total" (USR-06).
 * `visible` is the row count actually rendered, `tabTotal` the unfiltered size of the current tab,
 * `directoryTotal` every user in the directory across both tabs.
 */
export function describeUserCount(
  tab: UserTab,
  visible: number,
  tabTotal: number,
  directoryTotal: number,
): string {
  const noun = TAB_NOUN[tab];
  const scope =
    visible === tabTotal
      ? `${tabTotal} ${noun} ${tabTotal === 1 ? 'user' : 'users'}`
      : `${visible} of ${tabTotal} ${noun} ${tabTotal === 1 ? 'user' : 'users'}`;
  return `Showing ${scope} · ${directoryTotal} total in directory`;
}
